import { c } from 'react-compiler-runtime';
import { ChevronLeftIcon, ChevronRightIcon } from '@primer/octicons-react';
import { useState } from 'react';
import { Button } from '../internal/components/ButtonReset.js';
import { LiveRegionOutlet, LiveRegion, Message } from '../internal/components/LiveRegion.js';
import { warning } from '../utils/warning.js';
import { viewportRanges } from '../hooks/useResponsiveValue.js';
import { buildPaginationModel } from '../Pagination/model.js';
import classes from './Pagination.module.css.js';
import { clsx } from 'clsx';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden.js';

const defaultShowPages = {
  narrow: false
};
function Pagination(t0) {
  const $ = c(61);
  const {
    "aria-label": label,
    defaultPageIndex,
    id,
    onChange,
    pageSize: t1,
    showPages: t2,
    totalCount
  } = t0;
  const pageSize = t1 === undefined ? 25 : t1;
  const showPages = t2 === undefined ? defaultShowPages : t2;
  let t3;
  if ($[0] !== defaultPageIndex || $[1] !== onChange || $[2] !== pageSize || $[3] !== totalCount) {
    t3 = {
      defaultPageIndex,
      onChange,
      pageSize,
      totalCount
    };
    $[0] = defaultPageIndex;
    $[1] = onChange;
    $[2] = pageSize;
    $[3] = totalCount;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    pageIndex,
    pageStart,
    pageEnd,
    pageCount,
    hasPreviousPage,
    hasNextPage,
    selectPage,
    selectNextPage,
    selectPreviousPage
  } = usePagination(t3);
  let t4;
  if ($[5] !== showPages) {
    t4 = () => {
      if (typeof showPages !== "boolean") {
        return Object.keys(showPages).filter(key => !showPages[key]);
      }
      if (showPages) {
        return [];
      } else {
        return Object.keys(viewportRanges);
      }
    };
    $[5] = showPages;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const getViewportRangesToHidePages = t4;
  const t5 = pageIndex + 1;
  const t6 = !!showPages;
  let t7;
  if ($[7] !== pageCount || $[8] !== t5 || $[9] !== t6) {
    t7 = buildPaginationModel(pageCount, t5, t6, 1, 2);
    $[7] = pageCount;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const model = t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = /*#__PURE__*/jsx(LiveRegionOutlet, {});
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = clsx("TablePagination", classes.TablePagination);
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== pageEnd || $[14] !== pageStart || $[15] !== totalCount) {
    t10 = /*#__PURE__*/jsx(Range, {
      pageStart: pageStart,
      pageEnd: pageEnd,
      totalCount: totalCount
    });
    $[13] = pageEnd;
    $[14] = pageStart;
    $[15] = totalCount;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = clsx("TablePaginationSteps", classes.TablePaginationSteps);
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== getViewportRangesToHidePages) {
    t12 = getViewportRangesToHidePages();
    $[18] = getViewportRangesToHidePages;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = t12.join(" ");
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = clsx("TablePaginationAction", classes.TablePaginationAction);
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const t15 = hasPreviousPage ? true : undefined;
  const t16 = !hasPreviousPage ? true : undefined;
  let t17;
  if ($[21] !== hasPreviousPage || $[22] !== selectPreviousPage) {
    t17 = () => {
      if (!hasPreviousPage) {
        return;
      }
      selectPreviousPage();
    };
    $[21] = hasPreviousPage;
    $[22] = selectPreviousPage;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== hasPreviousPage) {
    t18 = hasPreviousPage ? /*#__PURE__*/jsx(ChevronLeftIcon, {}) : null;
    $[24] = hasPreviousPage;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  let t20;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = /*#__PURE__*/jsx("span", {
      children: "Previous"
    });
    t20 = /*#__PURE__*/jsx(VisuallyHidden, {
      children: "\xA0page"
    });
    $[26] = t19;
    $[27] = t20;
  } else {
    t19 = $[26];
    t20 = $[27];
  }
  let t21;
  if ($[28] !== t15 || $[29] !== t16 || $[30] !== t17 || $[31] !== t18) {
    t21 = /*#__PURE__*/jsx(Step, {
      children: /*#__PURE__*/jsxs(Button, {
        className: t14,
        type: "button",
        "data-has-page": t15,
        "aria-disabled": t16,
        onClick: t17,
        children: [t18, t19, t20]
      })
    });
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== model || $[34] !== selectPage) {
    let t23;
    if ($[36] !== selectPage) {
      t23 = (page, i) => {
        if (page.type === "BREAK") {
          return /*#__PURE__*/jsx(TruncationStep, {}, `truncation-${i}`);
        } else {
          if (page.type === "NUM") {
            return /*#__PURE__*/jsx(Step, {
              children: /*#__PURE__*/jsxs(Page, {
                active: !!page.selected,
                onClick: () => {
                  selectPage(page.num - 1);
                },
                children: [page.num, page.precedesBreak ? /*#__PURE__*/jsx(VisuallyHidden, {
                  children: "\u2026"
                }) : null]
              })
            }, i);
          }
        }
      };
      $[36] = selectPage;
      $[37] = t23;
    } else {
      t23 = $[37];
    }
    t22 = model.map(t23);
    $[33] = model;
    $[34] = selectPage;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = clsx("TablePaginationAction", classes.TablePaginationAction);
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  const t24 = hasNextPage ? true : undefined;
  const t25 = !hasNextPage ? true : undefined;
  let t26;
  if ($[39] !== hasNextPage || $[40] !== selectNextPage) {
    t26 = () => {
      if (!hasNextPage) {
        return;
      }
      selectNextPage();
    };
    $[39] = hasNextPage;
    $[40] = selectNextPage;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  let t28;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = /*#__PURE__*/jsx("span", {
      children: "Next"
    });
    t28 = /*#__PURE__*/jsx(VisuallyHidden, {
      children: "\xA0page"
    });
    $[42] = t27;
    $[43] = t28;
  } else {
    t27 = $[42];
    t28 = $[43];
  }
  let t29;
  if ($[44] !== hasNextPage) {
    t29 = hasNextPage ? /*#__PURE__*/jsx(ChevronRightIcon, {}) : null;
    $[44] = hasNextPage;
    $[45] = t29;
  } else {
    t29 = $[45];
  }
  let t30;
  if ($[46] !== t24 || $[47] !== t25 || $[48] !== t26 || $[49] !== t29) {
    t30 = /*#__PURE__*/jsx(Step, {
      children: /*#__PURE__*/jsxs(Button, {
        className: t23,
        type: "button",
        "data-has-page": t24,
        "aria-disabled": t25,
        onClick: t26,
        children: [t27, t28, t29]
      })
    });
    $[46] = t24;
    $[47] = t25;
    $[48] = t26;
    $[49] = t29;
    $[50] = t30;
  } else {
    t30 = $[50];
  }
  let t31;
  if ($[51] !== t13 || $[52] !== t21 || $[53] !== t22 || $[54] !== t30) {
    t31 = /*#__PURE__*/jsxs("ol", {
      className: t11,
      "data-hidden-viewport-ranges": t13,
      children: [t21, t22, t30]
    });
    $[51] = t13;
    $[52] = t21;
    $[53] = t22;
    $[54] = t30;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  let t32;
  if ($[56] !== id || $[57] !== label || $[58] !== t10 || $[59] !== t31) {
    t32 = /*#__PURE__*/jsxs(LiveRegion, {
      children: [t8, /*#__PURE__*/jsxs("nav", {
        "aria-label": label,
        className: t9,
        id: id,
        children: [t10, t31]
      })]
    });
    $[56] = id;
    $[57] = label;
    $[58] = t10;
    $[59] = t31;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  return t32;
}
function Range(t0) {
  const $ = c(12);
  const {
    pageStart,
    pageEnd,
    totalCount
  } = t0;
  const start = pageStart + 1;
  const end = pageEnd;
  const t1 = `Showing ${start} through ${end} of ${totalCount}`;
  let t2;
  if ($[0] !== t1) {
    t2 = /*#__PURE__*/jsx(Message, {
      value: t1
    });
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = clsx("TablePaginationRange", classes.TablePaginationRange);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = /*#__PURE__*/jsx(VisuallyHidden, {
      children: "\xA0through\xA0"
    });
    t5 = /*#__PURE__*/jsx("span", {
      "aria-hidden": "true",
      children: "\u2012"
    });
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== end || $[6] !== start || $[7] !== totalCount) {
    t6 = /*#__PURE__*/jsxs("p", {
      className: t3,
      children: [start, t4, t5, end, " of ", totalCount]
    });
    $[5] = end;
    $[6] = start;
    $[7] = totalCount;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t2 || $[10] !== t6) {
    t7 = /*#__PURE__*/jsxs(Fragment, {
      children: [t2, t6]
    });
    $[9] = t2;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
function TruncationStep() {
  const $ = c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = /*#__PURE__*/jsx("li", {
      "aria-hidden": "true",
      className: clsx("TablePaginationTruncationStep", classes.TablePaginationTruncationStep),
      children: "\u2026"
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function Step(t0) {
  const $ = c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = clsx("TablePaginationStep", classes.TablePaginationStep);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = /*#__PURE__*/jsx("li", {
      className: t1,
      children: children
    });
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function Page(t0) {
  const $ = c(7);
  const {
    active,
    children,
    onClick
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = clsx("TablePaginationPage", classes.TablePaginationPage);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = active ? true : undefined;
  const t3 = active ? true : undefined;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = /*#__PURE__*/jsx(VisuallyHidden, {
      children: "Page\xA0"
    });
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== children || $[3] !== onClick || $[4] !== t2 || $[5] !== t3) {
    t5 = /*#__PURE__*/jsxs(Button, {
      className: t1,
      type: "button",
      "data-active": t2,
      "aria-current": t3,
      onClick: onClick,
      children: [t4, children]
    });
    $[2] = children;
    $[3] = onClick;
    $[4] = t2;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
function usePagination(config) {
  const $ = c(24);
  const {
    defaultPageIndex,
    onChange,
    pageSize,
    totalCount
  } = config;
  const pageCount = Math.ceil(totalCount / pageSize);
  let t0;
  if ($[0] !== defaultPageIndex || $[1] !== pageCount) {
    t0 = () => {
      if (defaultPageIndex !== undefined) {
        if (defaultPageIndex >= 0 && defaultPageIndex < pageCount) {
          return defaultPageIndex;
        }
        process.env.NODE_ENV !== "production" ? warning(true, "<Pagination> expected `defaultPageIndex` to be less than the total number of pages. Instead, received a `defaultPageIndex` of %s with %s total pages.", defaultPageIndex, pageCount) : void 0;
      }
      return 0;
    };
    $[0] = defaultPageIndex;
    $[1] = pageCount;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [defaultIndex, setDefaultIndex] = useState(t0);
  const [pageIndex, setPageIndex] = useState(defaultIndex);
  const validDefaultPageCount = defaultPageIndex !== undefined && defaultPageIndex >= 0 && defaultPageIndex < pageCount;
  if (validDefaultPageCount && defaultIndex !== defaultPageIndex) {
    setDefaultIndex(defaultPageIndex);
    setPageIndex(defaultPageIndex);
    onChange === null || onChange === void 0 ? void 0 : onChange({
      pageIndex: defaultPageIndex
    });
  }
  const pageStart = pageIndex * pageSize;
  const pageEnd = Math.min((pageIndex + 1) * pageSize, totalCount);
  const hasNextPage = pageIndex + 1 < pageCount;
  const hasPreviousPage = pageIndex > 0;
  let t1;
  if ($[3] !== onChange || $[4] !== pageIndex) {
    t1 = function selectPage(newPageIndex) {
      if (pageIndex !== newPageIndex) {
        setPageIndex(newPageIndex);
        onChange === null || onChange === void 0 ? void 0 : onChange({
          pageIndex: newPageIndex
        });
      }
    };
    $[3] = onChange;
    $[4] = pageIndex;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const selectPage = t1;
  let t2;
  if ($[6] !== hasPreviousPage || $[7] !== pageIndex || $[8] !== selectPage) {
    t2 = function selectPreviousPage() {
      if (hasPreviousPage) {
        selectPage(pageIndex - 1);
      }
    };
    $[6] = hasPreviousPage;
    $[7] = pageIndex;
    $[8] = selectPage;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const selectPreviousPage = t2;
  let t3;
  if ($[10] !== hasNextPage || $[11] !== pageIndex || $[12] !== selectPage) {
    t3 = function selectNextPage() {
      if (hasNextPage) {
        selectPage(pageIndex + 1);
      }
    };
    $[10] = hasNextPage;
    $[11] = pageIndex;
    $[12] = selectPage;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const selectNextPage = t3;
  let t4;
  if ($[14] !== hasNextPage || $[15] !== hasPreviousPage || $[16] !== pageCount || $[17] !== pageEnd || $[18] !== pageIndex || $[19] !== pageStart || $[20] !== selectNextPage || $[21] !== selectPage || $[22] !== selectPreviousPage) {
    t4 = {
      pageIndex,
      pageStart,
      pageEnd,
      pageCount,
      hasNextPage,
      hasPreviousPage,
      selectPage,
      selectPreviousPage,
      selectNextPage
    };
    $[14] = hasNextPage;
    $[15] = hasPreviousPage;
    $[16] = pageCount;
    $[17] = pageEnd;
    $[18] = pageIndex;
    $[19] = pageStart;
    $[20] = selectNextPage;
    $[21] = selectPage;
    $[22] = selectPreviousPage;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  return t4;
}

export { Pagination };
